// [PATCH ddv-konto-lib v1]
// EN VIR RESNICE za konto DDV na frontendu.
//
// Zrcalo backend kontiranje_service.py::ddv_konto() (izmerjeno 24.08.2026,
// vrstice 160-209). Veriga je NAMENOMA znakovno enaka backendovi:
//     matrika    konto_ddv_<kategorija>_<smer>_<stopnja>
//  -> stari kljuc (najprej s stopnjo, nato brez nje)
//  -> genericni   konto_ddv_genericni_<smer>
//  -> trdi fallback 160000 / 260000
//
// ZAKAJ obstaja: pred tem so bile na frontendu TRI razlicne kopije te
// verige, vsaka drugacna:
//   DdvKnjiga.tsx::kontoZaStopnjo    matrika -> star -> genericni (samo dom)
//   DokumentiPage.tsx::kontoStopnje  SAMO stari ploski kljuci
//   DokumentiPage.tsx EU veja        GOLA imena brez stopnje
// Zadnja je bila napacna: profil hrani samo kljuce S STOPNJO
// (konto_ddv_eu_stor_vstopni_22), golega imena ni, zato je fallback vrnil
// DOMACI konto_ddv_22 namesto EU konta.

export type DdvKategorija =
  'dom' | 'so' | '76a' | 'eu_blago' | 'eu_stor' | 'uvoz' | 'so_uvoz' | 'pavsal'
  | 'oss';  /* [PATCH oss-temelji v1] */
export type DdvSmer = 'vstopni' | 'obracunani';

const GENERICNI_VSTOPNI = '160000';
const GENERICNI_OBRACUNANI = '260000';

// zrcalo backend _ddv_stopnja_kljuc(): toleranca 0,1 odstotne tocke,
// neznana/neveljavna stopnja -> '22' (enako kot backend)
export const stopnjaKljuc = (stopnja: any): string => {
  const s = Number(stopnja);
  if (!Number.isFinite(s)) return '22';
  const mejne: Array<[number, string]> = [[9.5, '95'], [5, '5'], [8, '8']];
  for (const [m, k] of mejne) { if (Math.abs(s - m) < 0.1) return k; }
  return '22';
};

// zrcalo backend _DDV_STARI_KLJUCI (dobesedno, izmerjeno 24.08.2026)
const STARI: Record<string, string> = {
  'dom|vstopni|22': 'konto_ddv_22',
  'dom|vstopni|95': 'konto_ddv_95',
  'dom|vstopni|5': 'konto_ddv_5',
  '76a|vstopni': 'konto_ddv_76a_vstopni',
  '76a|obracunani': 'konto_ddv_76a_obracunani',
  'eu_blago|vstopni': 'konto_ddv_eu_blago_vstopni',
  'eu_blago|obracunani': 'konto_ddv_eu_blago_obracunani',
  'eu_stor|vstopni': 'konto_ddv_eu_stor_vstopni',
  'eu_stor|obracunani': 'konto_ddv_eu_stor_obracunani',
};

export const ddvKonto = (konti: any, kategorija: DdvKategorija,
                         stopnja: any, smer: DdvSmer): string => {
  const dk = konti || {};
  /* [PATCH oss-temelji v1] OSS nima konta v profilu podjetja: konto DDV je
     odvisen od DRZAVE POTROSNJE in stopnje, zato pride izkljucno iz
     oss_util.konti_za() (nastavitve Nastavitve -> Izdani racuni -> OSS
     drzave). Prazen niz je namerna vrednost -- klicoci pusti polje prazno
     in izpise opozorilo, nikoli ne pade na genericni domaci konto.
     Zrcalo backendove ddv_konto(), veja 'oss'. */
  if (kategorija === 'oss') return '';
  const sk = stopnjaKljuc(stopnja);
  const v = dk['konto_ddv_' + kategorija + '_' + smer + '_' + sk];
  if (v) return String(v).trim();
  const star = STARI[kategorija + '|' + smer + '|' + sk] || STARI[kategorija + '|' + smer];
  if (star && dk[star]) return String(dk[star]).trim();
  if (smer === 'vstopni') {
    return String(dk.konto_ddv_genericni_vstopni || GENERICNI_VSTOPNI).trim();
  }
  return String(dk.konto_ddv_genericni_obracunani || GENERICNI_OBRACUNANI).trim();
};

// Preslikava vrste iz VRSTE_DDV v kategorijo backendove matrike.
// POZOR: oproscene vrste (OPROSCEN*, NI_PREDMET, NEOBDAVCEN) vrnejo 'dom' —
// NAMENOMA. Backend dokumenti.py:1350 zanje prav tako klice ddv_konto(...,'dom',...)
// in v konto_ddv zapise domaci konto. Ce bi tu vrnili prazno, bi se FE in BE
// razsla; te vrstice tako ali tako nimajo DDV knjizbe (znesek_ddv = 0).
export const kategorijaIzVrste = (vrsta: string): DdvKategorija => {
  const v = String(vrsta || '');
  /* [PATCH oss-temelji v1] OSS pred ostalimi: IZD_OSS_* ima svojo kategorijo,
     ker konto ne pride iz profila, ampak iz nastavitev po drzavi.
     Odlocitev Janeza 03.09. (porocilo koraka 1, V1). */
  if (v === 'IZD_OSS_BLAGO' || v === 'IZD_OSS_STORITEV') return 'oss';
  if (v.indexOf('EU_STOR') === 0) return 'eu_stor';
  if (v.indexOf('EU_BLAGO') === 0) return 'eu_blago';
  if (v.indexOf('76A') === 0) return '76a';
  if (v === 'UVOZ') return 'uvoz';
  return 'dom';
};

// KIR = obracunani (izstopni, obveznost); KPR in vse ostalo prejeto = vstopni.
// Izdani racuni so vedno obracunani.
export const smerIzVrste = (vrsta: string, jeIzdani: boolean): DdvSmer => {
  const v = String(vrsta || '');
  if (v.length >= 4 && v.substring(v.length - 4) === '_KIR') return 'obracunani';
  if (jeIzdani) return 'obracunani';
  return 'vstopni';
};

// Edina funkcija, ki jo klicejo komponente.
export const kontoZaVrsto = (konti: any, vrsta: string, stopnja: any,
                             jeIzdani: boolean): string =>
  ddvKonto(konti, kategorijaIzVrste(vrsta), stopnja, smerIzVrste(vrsta, jeIzdani));
